import type { ResumeData } from "@/types/resume";
import { formatDate, getOrderedSections } from "./templateUtils";
import { bodyFont, headingFont } from "./useResumeFonts";

export function TemplateMinimalExecutive({ data, overridePrimary }: { data: ResumeData; overridePrimary?: string }) {
  const { personalInfo: p } = data;
  const PRIMARY = overridePrimary || "#333333";
  const FONT_BODY = bodyFont(data, "Georgia, 'Times New Roman', serif");
  const FONT_HEADING = headingFont(data, FONT_BODY);

  const sectionRenderers: Record<string, React.ReactNode> = {
    summary: data.summary ? <Section color={PRIMARY} key="summary" title="Profile"><p className="italic">{data.summary}</p></Section> : null,
    workExperience: data.workExperience.length > 0 ? (
      <Section color={PRIMARY} key="workExperience" title="Experience">
        {data.workExperience.map((exp) => (
          <div key={exp.id} className="mb-4 last:mb-0">
            <div className="flex justify-between"><strong>{exp.jobTitle}</strong><span className="text-[10px]" style={{ color: "#475569" }}>{exp.startDate && formatDate(exp.startDate)} — {exp.currentlyWorking ? "Present" : exp.endDate ? formatDate(exp.endDate) : ""}</span></div>
            {exp.company && <p className="italic" style={{ color: "#555" }}>{exp.company}{exp.location ? `, ${exp.location}` : ""}</p>}
            {exp.responsibilities.filter(Boolean).length > 0 && <ul className="list-none mt-1 space-y-0.5">{exp.responsibilities.filter(Boolean).map((r, i) => <li key={i} className="pl-3 relative before:content-['—'] before:absolute before:left-0 before:text-gray-400">{r}</li>)}</ul>}
          </div>
        ))}
      </Section>
    ) : null,
    education: data.education.length > 0 ? (
      <Section color={PRIMARY} key="education" title="Education">
        {data.education.map((edu) => (
          <div key={edu.id} className="mb-2 last:mb-0">
            <div className="flex justify-between"><strong>{edu.degree}</strong>{edu.graduationDate && <span className="text-[10px]" style={{ color: "#475569" }}>{formatDate(edu.graduationDate)}</span>}</div>
            {edu.institution && <p className="italic" style={{ color: "#555" }}>{edu.institution}{edu.location ? `, ${edu.location}` : ""}</p>}
          </div>
        ))}
      </Section>
    ) : null,
    skills: data.skills.length > 0 ? (
      <Section color={PRIMARY} key="skills" title="Expertise">
        <p>{data.skills.flatMap((g) => g.skills).filter(Boolean).join("  ·  ")}</p>
      </Section>
    ) : null,
    projects: data.projects.length > 0 ? <Section color={PRIMARY} key="projects" title="Projects">{data.projects.map((proj) => <div key={proj.id} className="mb-2 last:mb-0"><strong>{proj.title}</strong>{proj.role && <span> — {proj.role}</span>}{proj.description && <p className="mt-0.5">{proj.description}</p>}</div>)}</Section> : null,
    certifications: data.certifications.length > 0 ? <Section color={PRIMARY} key="certifications" title="Certifications">{data.certifications.map((c) => <p key={c.id}>{c.url ? <a href={c.url} target="_blank" rel="noopener noreferrer" style={{ color: PRIMARY }}>{c.name}</a> : c.name}{c.issuer ? ` — ${c.issuer}` : ""}</p>)}</Section> : null,
    languages: data.languages.length > 0 ? <Section color={PRIMARY} key="languages" title="Languages"><p>{data.languages.map((l) => `${l.language} (${l.proficiency})`).join("  ·  ")}</p></Section> : null,
    achievements: data.achievements.filter(Boolean).length > 0 ? <Section color={PRIMARY} key="achievements" title="Achievements"><ul className="list-none space-y-0.5">{data.achievements.filter(Boolean).map((a, i) => <li key={i} className="pl-3 relative before:content-['—'] before:absolute before:left-0 before:text-gray-400">{a}</li>)}</ul></Section> : null,
  };

  return (
    <div className="p-8 text-xs leading-relaxed" style={{ fontFamily: FONT_BODY, color: "#1a1a1a", background: "#ffffff" }}>
      <div className="text-center mb-6">
        {p.photoUrl && <img src={p.photoUrl} alt="" width={80} height={80} loading="lazy" decoding="async" className="w-20 h-20 rounded-full object-cover mx-auto mb-3" style={{ border: "2px solid #ccc" }} />}
        {p.fullName && <h1 className="text-2xl font-normal tracking-wide mb-1" style={{ letterSpacing: "0.15em", fontFamily: FONT_HEADING }}>{p.fullName.toUpperCase()}</h1>}
        {p.professionalTitle && <p className="text-sm italic" style={{ color: "#555" }}>{p.professionalTitle}</p>}
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mt-2 text-[10px]" style={{ color: "#475569" }}>
          {p.email && <span>{p.email}</span>}
          {p.phone && <span>{p.phone}</span>}
          {p.city && <span>{p.city}{p.country ? `, ${p.country}` : ""}</span>}
          {p.linkedin && <span>{p.linkedin}</span>}
        </div>
      </div>
      {getOrderedSections(data).map((key) => sectionRenderers[key])}
    </div>
  );
}

function Section({ title, children, color = "#333" }: { title: string; children: React.ReactNode; color?: string }) {
  return (
    <div className="mb-5">
      <h2 className="text-[11px] font-normal uppercase tracking-[0.2em] mb-2 pb-1" style={{ color, borderBottom: "1px solid #ccc" }}>{title}</h2>
      {children}
    </div>
  );
}
